/**
 * SCENE.JSX - Scène 3D principale
 * 
 * Ce composant contient tous les éléments 3D de la scène :
 * - OrbitControls pour manipuler la caméra
 * - Lumières pour éclairer les modèles
 * - Floor avec dégradé et glow
 * - Radio avec texture gradientAtlas
 * - Saucisse avec texture gradientAtlas
 * 
 * RESPONSABILITÉS :
 * - Configuration des contrôles de caméra
 * - Organisation des éléments 3D
 * - Gestion de l'éclairage
 */

import { OrbitControls, useHelper } from '@react-three/drei'
import { useControls, button } from 'leva'
import { useRef, useEffect } from 'react'
import * as THREE from 'three'
import Floor from './Floor'
import Radio from './Radio'
import Sausage from './Sausage'
import CameraDebug from './CameraDebug'
import SnapDebug from './SnapDebug'
import KeyboardControls from './KeyboardControls'
import StatsMonitor from './StatsMonitor'
import { useKeyboardSounds } from '../hooks/useKeyboardSounds'
import { useDebugMode } from '../hooks/useDebugMode'

/**
 * Props :
 * - background (optionnel) : Configuration du background à passer au Floor
 */
function Scene({ background = null }) {
  // ============================================
  // MODE DEBUG
  // ============================================
  // Détecte si l'URL contient #debug
  const isDebugMode = useDebugMode()

  // ============================================
  // ACTIVATION DES SONS CLAVIER
  // ============================================
  // Active les sons déclenchés par touches clavier
  // Configurés dans config/sounds.js (propriété keyboard)
  useKeyboardSounds()

  // Référence à la lumière directionnelle pour le helper
  const directionalLightRef = useRef()
  
  // Référence pour tracker le background précédent
  const previousBackgroundIdRef = useRef(null)

  // Contrôles Leva pour tester les performances
  const { stressTest } = useControls('Performance', {
    stressTest: {
      value: 0,
      min: 0,
      max: 10000,        // 1000 sphères max (chacune = 2048 triangles !)
      step: 50,         // Incrément de 50 pour aller plus vite
      label: '🔥 Stress Test (spheres)'
    },
    info: {
      value: 'Chaque sphère = 2048 triangles. 1000 sphères = ~2 millions de triangles !',
      editable: false,
      label: 'ℹ️ Info'
    }
  }, { collapsed: true })

  // Couleur d'ombre par défaut (peut être overridée par le background)
  const defaultShadowColor = background?.shadowColor || '#6f0559'

  // Contrôles Leva pour les ombres
  const [shadowControls, setShadowControls] = useControls('Shadows', () => ({
    shadowMapSize: {
      value: 2048,
      min: 512,
      max: 4096,
      step: 512,
      label: '📐 Map Size'
    },
    '---1': { value: '--- Camera Bounds ---', editable: false },
    shadowCameraLeft: {
      value: -10,
      min: -50,
      max: 0,
      step: 1,
      label: 'Camera Left'
    },
    shadowCameraRight: {
      value: 10,
      min: 0,
      max: 50,
      step: 1,
      label: 'Camera Right'
    },
    shadowCameraTop: {
      value: 10,
      min: 0,
      max: 50,
      step: 1,
      label: 'Camera Top'
    },
    shadowCameraBottom: {
      value: -10,
      min: -50,
      max: 0,
      step: 1,
      label: 'Camera Bottom'
    },
    '---2': { value: '--- Depth ---', editable: false },
    shadowCameraNear: {
      value: 0.5,
      min: 0.1,
      max: 10,
      step: 0.1,
      label: 'Camera Near'
    },
    shadowCameraFar: {
      value: 50,
      min: 10,
      max: 200,
      step: 5,
      label: 'Camera Far'
    },
    '---3': { value: '--- Quality ---', editable: false },
    shadowBias: {
      value: -0.001,
      min: -0.001,
      max: 0.001,
      step: 0.00001,
      label: 'Shadow Bias'
    },
    shadowColor: {  // ← NOUVEAU contrôle
      value: defaultShadowColor,
      label: '🎨 Color'
    },
    shadowOpacity: {
      // value: 0.4,
      value: 0.3,
      min: 0,
      max: 1,
      step: 0.05,
      label: '🌑 Opacity'
    },
    '---4': { value: '--- Debug ---', editable: false },
    showHelper: {
      value: true,
      label: '👁️ Show Helper'
    },
    '---5': { value: '--- Actions ---', editable: false },
    logValues: button(() => {
      // Récupère les valeurs depuis le DOM
      const getMapSize = parseFloat(document.getElementById('Shadows.shadowMapSize')?.value || shadowMapSize)
      const getCameraLeft = parseFloat(document.getElementById('Shadows.shadowCameraLeft')?.value || shadowCameraLeft)
      const getCameraRight = parseFloat(document.getElementById('Shadows.shadowCameraRight')?.value || shadowCameraRight)
      const getCameraTop = parseFloat(document.getElementById('Shadows.shadowCameraTop')?.value || shadowCameraTop)
      const getCameraBottom = parseFloat(document.getElementById('Shadows.shadowCameraBottom')?.value || shadowCameraBottom)
      const getCameraNear = parseFloat(document.getElementById('Shadows.shadowCameraNear')?.value || shadowCameraNear)
      const getCameraFar = parseFloat(document.getElementById('Shadows.shadowCameraFar')?.value || shadowCameraFar)
      const getBias = parseFloat(document.getElementById('Shadows.shadowBias')?.value || shadowBias)
      const getOpacity = parseFloat(document.getElementById('Shadows.shadowOpacity')?.value || shadowOpacity)
      
      console.log('🌑 Shadows - Valeurs actuelles :')
      console.log('\n📐 Shadow Map :')
      console.log(`  mapSize: ${getMapSize}`)
      console.log('\n📦 Camera Bounds :')
      console.log(`  left: ${getCameraLeft}`)
      console.log(`  right: ${getCameraRight}`)
      console.log(`  top: ${getCameraTop}`)
      console.log(`  bottom: ${getCameraBottom}`)
      console.log('\n🎬 Camera Depth :')
      console.log(`  near: ${getCameraNear}`)
      console.log(`  far: ${getCameraFar}`)
      console.log('\n✨ Quality :')
      console.log(`  bias: ${getBias}`)
      console.log(`  opacity: ${getOpacity}`)
      console.log('\n📋 Code à copier :')
      console.log(`shadowMapSize: ${getMapSize},`)
      console.log(`shadowCamera: { left: ${getCameraLeft}, right: ${getCameraRight}, top: ${getCameraTop}, bottom: ${getCameraBottom}, near: ${getCameraNear}, far: ${getCameraFar} },`)
      console.log(`shadowBias: ${getBias},`)
      console.log(`shadowOpacity: ${getOpacity}`)
    }, { label: '📋 Log Values' })
  }), { collapsed: true })

  // Extraction des valeurs de shadowControls
  const {
    shadowMapSize,
    shadowCameraNear,
    shadowCameraFar,
    shadowCameraLeft,
    shadowCameraRight,
    shadowCameraTop,
    shadowCameraBottom,
    shadowBias,
    shadowColor, 
    shadowOpacity,
    showHelper
  } = shadowControls

  // Mise à jour du shadowColor UNIQUEMENT quand l'ID du background change
  // Permet de changer la couleur d'ombre lors du changement de background
  // tout en permettant les modifications manuelles dans Leva
  useEffect(() => {
    const currentBackgroundId = background?.id || null
    
    // Ne mettre à jour que si l'ID du background a changé
    if (currentBackgroundId !== previousBackgroundIdRef.current) {
      previousBackgroundIdRef.current = currentBackgroundId
      
      if (background?.shadowColor) {
        setShadowControls({ shadowColor: background.shadowColor })
      }
    }
  }, [background, setShadowControls])

  // Contrôles Leva pour l'éclairage
  const {
    ambientIntensity,
    ambientColor,
    dirLight1Intensity,
    dirLight1Color,
    dirLight1X,
    dirLight1Y,
    dirLight1Z,
    dirLight2Intensity,
    dirLight2Color,
    dirLight2X,
    dirLight2Y,
    dirLight2Z
  } = useControls('Lighting', {
    ambientIntensity: {
      // value: 0.8,
      value: 1,
      min: 0,
      max: 2,
      step: 0.1,
      label: '💡 Intensité Ambiante'
    },
    ambientColor: {
      value: '#ffffff',
      label: '🎨 Couleur Ambiante'
    },
    // Base value :
    // -  - dirLight1: { position: [-4.5, 0.5, 3.5], intensity: 1.4, color: "#ffffff" },
    '---1': { value: '--- Lumière 1 ---', editable: false },
    dirLight1Intensity: {
      value: 1.4,
      min: 0,
      max: 3,
      step: 0.1,
      label: 'Intensité 1'
    },
    dirLight1Color: {
      value: '#ffffff',
      label: '🎨 Couleur 1'
    },
    dirLight1X: {
      // value: -4.5,
      value: -1,
      min: -10,
      max: 10,
      step: 0.5,
      label: 'Position X1'
    },
    dirLight1Y: {
      // value: 0.5,
      value: -1,
      min: -10,
      max: 10,
      step: 0.5,
      label: 'Position Y1'
    },
    dirLight1Z: {
      value: 3.5,
      min: -10,
      max: 10,
      step: 0.5,
      label: 'Position Z1'
    },
    // Base value :
    // -  - dirLight2: { position: [-3.5, 1, 1.5], intensity: 0.7, color: "#e3e7eb" }
    '---2': { value: '--- Lumière 2 ---', editable: false },
    dirLight2Intensity: {
      value: 0.7,
      min: 0,
      max: 3,
      step: 0.1,
      label: 'Intensité 2'
    },
    dirLight2Color: {
      value: '#ffffff',
      label: '🎨 Couleur 2'
    },
    dirLight2X: {
      value: -3.5,
      min: -10,
      max: 10,
      step: 0.5,
      label: 'Position X2'
    },
    dirLight2Y: {
      value: 1,
      min: -10,
      max: 10,
      step: 0.5,
      label: 'Position Y2'
    },
    dirLight2Z: {
      value: 1.5,
      min: -10,
      max: 10,
      step: 0.5,
      label: 'Position Z2'
    },
    '---3': { value: '--- Actions ---', editable: false },

    // ==================
    // BOUTON LOG VALUES
    // ==================
    
    // Récupère les valeurs depuis le DOM (inputs HTML de Leva)
    // Méthode simple et robuste : on lit directement dans les inputs générés par Leva
    logValues: button(() => {
      // Récupère toutes les valeurs d'éclairage depuis les inputs HTML
      // Format des IDs : "Lighting.nomDeLaVariable"
      const getAmbientIntensity = parseFloat(document.getElementById('Lighting.ambientIntensity')?.value || ambientIntensity)
      const getAmbientColor = document.getElementById('Lighting.ambientColor')?.value || ambientColor
      
      const getDirLight1X = parseFloat(document.getElementById('Lighting.dirLight1X')?.value || dirLight1X)
      const getDirLight1Y = parseFloat(document.getElementById('Lighting.dirLight1Y')?.value || dirLight1Y)
      const getDirLight1Z = parseFloat(document.getElementById('Lighting.dirLight1Z')?.value || dirLight1Z)
      const getDirLight1Intensity = parseFloat(document.getElementById('Lighting.dirLight1Intensity')?.value || dirLight1Intensity)
      const getDirLight1Color = document.getElementById('Lighting.dirLight1Color')?.value || dirLight1Color
      
      const getDirLight2X = parseFloat(document.getElementById('Lighting.dirLight2X')?.value || dirLight2X)
      const getDirLight2Y = parseFloat(document.getElementById('Lighting.dirLight2Y')?.value || dirLight2Y)
      const getDirLight2Z = parseFloat(document.getElementById('Lighting.dirLight2Z')?.value || dirLight2Z)
      const getDirLight2Intensity = parseFloat(document.getElementById('Lighting.dirLight2Intensity')?.value || dirLight2Intensity)
      const getDirLight2Color = document.getElementById('Lighting.dirLight2Color')?.value || dirLight2Color
      
      console.log('💡 Lighting - Valeurs actuelles :')
      console.log('\n☀️ Lumière Ambiante :')
      console.log(`  intensity: ${getAmbientIntensity}`)
      console.log(`  color: "${getAmbientColor}"`)
      console.log('\n💡 Lumière Directionnelle 1 :')
      console.log(`  position: [${getDirLight1X}, ${getDirLight1Y}, ${getDirLight1Z}]`)
      console.log(`  intensity: ${getDirLight1Intensity}`)
      console.log(`  color: "${getDirLight1Color}"`)
      console.log('\n💡 Lumière Directionnelle 2 :')
      console.log(`  position: [${getDirLight2X}, ${getDirLight2Y}, ${getDirLight2Z}]`)
      console.log(`  intensity: ${getDirLight2Intensity}`)
      console.log(`  color: "${getDirLight2Color}"`)
      console.log('\n📋 Code à copier :')
      console.log(`ambientIntensity: ${getAmbientIntensity},`)
      console.log(`ambientColor: "${getAmbientColor}",`)
      console.log(`dirLight1: { position: [${getDirLight1X}, ${getDirLight1Y}, ${getDirLight1Z}], intensity: ${getDirLight1Intensity}, color: "${getDirLight1Color}" },`)
      console.log(`dirLight2: { position: [${getDirLight2X}, ${getDirLight2Y}, ${getDirLight2Z}], intensity: ${getDirLight2Intensity}, color: "${getDirLight2Color}" }`)
    }, { label: '📋 Log Values' })
  }, { collapsed: true })

  // Contrôles Leva pour OrbitControls
  const {
    enableMouseControls,
    minDistance,
    maxDistance,
    minPolarAngle,
    maxPolarAngle,
    rotateSpeed,
    zoomSpeed,
    panSpeed,
    dampingFactor
  } = useControls('OrbitControls', {
    enableMouseControls: {
      value: true,
      label: '🖱️ Activer Souris'
    },
    '---1': { value: '--- Limites ---', editable: false },
    minDistance: {
      value: 14,
      min: 1,
      max: 50,
      step: 1,
      label: 'Min Distance'
    },
    maxDistance: {
      value: 29,
      min: 10,
      max: 100,
      step: 1,
      label: 'Max Distance'
    },
    minPolarAngle: {
      value: 0,
      min: 0,
      max: Math.PI,
      step: 0.01,
      label: 'Min Polar Angle'
    },
    maxPolarAngle: {
      value: 1.44,
      min: 0,
      max: Math.PI,
      step: 0.01,
      label: 'Max Polar Angle'
    },
    '---2': { value: '--- Vitesses ---', editable: false },
    rotateSpeed: {
      value: 0.5,
      min: 0,
      max: 2,
      step: 0.1,
      label: 'Rotation Speed'
    },
    zoomSpeed: {
      value: 0.5,
      min: 0,
      max: 2,
      step: 0.1,
      label: 'Zoom Speed'
    },
    panSpeed: {
      value: 0.5,
      min: 0,
      max: 2,
      step: 0.1,
      label: 'Pan Speed'
    },
    '---3': { value: '--- Damping ---', editable: false },
    dampingFactor: {
      value: 0.05,
      min: 0,
      max: 0.2,
      step: 0.001,
      label: 'Damping Factor'
    }
  },
  { collapsed: true }
  )

  // Helper pour visualiser la direction de la lumière (debug)
  // Le helper orange montre la direction et la position de la lumière
  // Visible uniquement en mode debug (#debug dans l'URL) ET si showHelper est activé
  useHelper(isDebugMode && showHelper && directionalLightRef, THREE.DirectionalLightHelper, 1, 'orange')

  return (
    <>
      {/* 
        OrbitControls - Configuration via Leva "OrbitControls"
        
        CONTRÔLES SOURIS :
        - Clic gauche + glisser : Rotation
        - Molette : Zoom (limites configurables)
        - Clic droit + glisser : Pan
        
        CONTRÔLES CLAVIER :
        - Voir KeyboardControls pour les deux modes (standard/zoom)
        
        PARAMÈTRES LEVA :
        - Activer/désactiver la souris
        - Limites de distance (zoom)
        - Limites d'angle polaire (rotation verticale)
        - Vitesses de rotation, zoom, pan
        - Facteur de damping
      */}
      <OrbitControls
        enabled={enableMouseControls}
        makeDefault
        enableDamping={true}
        dampingFactor={dampingFactor}
        minDistance={minDistance}
        maxDistance={maxDistance}
        minPolarAngle={minPolarAngle}
        maxPolarAngle={maxPolarAngle}
        rotateSpeed={rotateSpeed}
        zoomSpeed={zoomSpeed}
        panSpeed={panSpeed}
        enableRotate={true}
        enableZoom={true}
        enablePan={true}
        target={[0, 0, 0]}
      />

      {/* Contrôles de debug pour la caméra (visible dans Leva) */}
      <CameraDebug />

      {/* Outil de snapshot pour capturer toutes les valeurs de la scène */}
      <SnapDebug />

      {/* Contrôles clavier pour la caméra (Phase 4) */}
      <KeyboardControls />

      {/* Monitoring des performances en temps réel */}
      <StatsMonitor />

      {/* 
        Lumières configurables via Leva "Lighting"
        - Ambiante : Éclairage général avec couleur personnalisable
        - Directionnelle 1 : Lumière principale avec ombres et couleur
        - Directionnelle 2 : Lumière d'appoint avec couleur
      */}
      <ambientLight 
        intensity={ambientIntensity} 
        color={ambientColor}
      />
      <directionalLight 
        ref={directionalLightRef}
        position={[dirLight1X, dirLight1Y, dirLight1Z]} 
        intensity={dirLight1Intensity}
        color={dirLight1Color}
        castShadow 
        shadow-mapSize={[shadowMapSize, shadowMapSize]}     // Résolution (contrôlée par Leva)
        shadow-camera-near={shadowCameraNear}               // Near plane
        shadow-camera-far={shadowCameraFar}                 // Far plane
        shadow-camera-left={shadowCameraLeft}               // Limites de la zone d'ombres
        shadow-camera-right={shadowCameraRight}
        shadow-camera-top={shadowCameraTop}
        shadow-camera-bottom={shadowCameraBottom}
        shadow-bias={shadowBias}                            // Anti-artefacts
      />
      <directionalLight 
        position={[dirLight2X, dirLight2Y, dirLight2Z]} 
        intensity={dirLight2Intensity}
        color={dirLight2Color}
      />

      {/* Floor avec dégradé et glow (Phase 1) */}
      <Floor background={background} />
      
      {/* Plan invisible pour recevoir les ombres (le shader custom du Floor ne les gère pas) */}
      {/* Avec Z-up, le plan XY est déjà horizontal, pas besoin de rotation */}
      <mesh 
        position={[0, 0, 0.01]}  // Légèrement au-dessus en Z (car Z-up)
        receiveShadow
      >
        <planeGeometry args={[20, 20]} />  {/* Plus grand pour couvrir toute la zone */}
        <shadowMaterial 
          opacity={shadowOpacity} 
          transparent
          color={shadowColor}
        />
      </mesh>

      {/* Radio avec contrôles de position et rotation dans Leva */}
      <Radio scale={1} />

      {/* Saucisse avec texture gradientAtlas */}
      <Sausage scale={1} />

      {/* Stress Test : Affiche des sphères pour tester le FPS */}
      {Array.from({ length: stressTest }).map((_, i) => (
        <mesh
          key={i}
          position={[
            Math.cos(i * 0.5) * 3,
            Math.sin(i * 0.5) * 3,
            Math.sin(i * 0.3) * 2
          ]}
          castShadow
        >
          <sphereGeometry args={[0.2, 32, 32]} />
          <meshStandardMaterial color={`hsl(${i * 10}, 70%, 50%)`} />
        </mesh>
      ))}
    </>
  )
}

export default Scene

